<template>
  <v-expansion-panels accordion v-model="showPanel" class="base-shadow">
    <v-expansion-panel expand>
      <v-expansion-panel-header>
        <content-line icon="custom/text" text="Manage Presentation" />
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <div class="pa-4">
          <field-block title="Add your own slides.com presentation!">
            <v-text-field
              type="text"
              v-model="customUrlModel"
              label="Slides.com URL"
              placeholder="https://slides.com/account/name"
              class="background-dark"
            />
          </field-block>

          <field-block
            class="mb-4 mt-5"
            title="Select the desired presentation."
          >
            <v-select
              label="Presentation Url"
              v-model="context.state.presentationUrl"
              :items="presentationsList"
              @change="updatePresentation"
              class="background-dark"
            />
          </field-block>

          <div v-if="context.state.presentationUrl">
            <iframe
              width="640"
              height="400"
              class="presentation-iframe"
              :src="
                context.state.presentationUrl + '/embed?postMessageEvents=true'
              "
              webkitallowfullscreen
              mozallowfullscreen
              allowfullscreen
              scrolling="no"
              frameborder="0"
            >
            </iframe>
          </div>

          <v-btn
            class="mt-4"
            :class="showPresentation ? '' : 'primary'"
            @click.prevent="togglePresentation()"
          >
            {{ showPresentation ? 'Hide' : 'Show' }}
          </v-btn>

          <v-btn
            class="mt-4 ml-3"
            text
            color="primary"
            @click.prevent="$emit('close')"
          >
            Close
          </v-btn>
        </div>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { mapState } from 'vuex';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

const customUrlStorageKey = 'roomsCustomPresentationUrl';

export default {
  name: 'ActionPresentationControls',
  props: {
    context: {
      type: Object,
      default: () => ({}),
    },
    actionData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      localPresentationState: null,
      showPanel: 0,
    };
  },
  computed: {
    ...mapState('rooms', ['roomInfo']),
    showPresentation() {
      return this.context.state.show;
    },
    customUrlModel: {
      get() {
        return (
          this.context.state.customUrl ||
          localStorage.getItem(customUrlStorageKey)
        );
      },
      set(value) {
        if (this.validSlidesUrl(value)) {
          this.context.state.customUrl = value;
          localStorage.setItem(customUrlStorageKey, value);
        }
      },
    },

    presentationsList() {
      const officePattern = this.roomInfo?.on ?? '';
      const customUrl = this.customUrlModel;

      const presentations = [...this.actionData.presentations];

      if (this.validSlidesUrl(customUrl)) {
        presentations.unshift({ text: customUrl, value: customUrl });
      }

      return presentations.filter((p) => {
        return !p?.if || p.if(officePattern);
      });
    },

    storeKey() {
      const { room, identity } = this.roomInfo;
      return ['presentation', room, identity].join('-');
    },
  },
  watch: {
    'context.state.presentationState'(val) {
      if (val && !isEqual(val, this.localPresentationState)) {
        this.updateSliderPosition(val);
      }
    },
  },
  methods: {
    togglePresentation() {
      this.context.state.show = !this.showPresentation;
      this.$emit('update');
    },

    validSlidesUrl(url) {
      url = url || '';
      return url.startsWith('https://') && url.includes('slides.com');
    },

    updatePresentation() {
      this.$emit('update');
    },

    handlePresentationEvents: debounce(function (event) {
      if (!event?.origin || !event.origin.includes('slides.com')) return false;

      const data = JSON.parse(event.data);
      const { namespace, eventName, state } = data || {};
      if (namespace === 'reveal') {
        if (eventName === 'ready') {
          this.extractPresentationState(true);
        }

        if (eventName === 'slidechanged') {
          this.savePresentationState(state);
          this.updatePresentationState(state);
        }
      }
    }, 50),

    savePresentationState(state) {
      localStorage.setItem(
        this.storeKey,
        JSON.stringify({
          state,
          url: this.context.state.presentationUrl,
        })
      );
    },

    updatePresentationState(state) {
      this.localPresentationState = state;
      this.context.state.presentationState = state;
      this.$emit('update', this.context);
    },

    async extractPresentationState(ready = false) {
      await this.$nextTick();

      const presentationState = localStorage.getItem(this.storeKey);

      if (presentationState) {
        const { state, url } = JSON.parse(presentationState);

        if (!ready) {
          this.context.state.presentationUrl = url;
          return;
        }

        if (ready && url === this.context.state.presentationUrl) {
          this.updateSliderPosition(state);
          this.updatePresentationState(state);
        }
      }
    },

    updateSliderPosition({ indexh, indexv }) {
      const frame = this.$el.querySelector('.presentation-iframe');
      const data = JSON.stringify({ method: 'slide', args: [indexh, indexv] });
      if (frame) {
        frame.contentWindow.postMessage(data, '*');
      }
    },

    assignData() {
      if (!this.context.state.presentationUrl) {
        this.context.state.presentationUrl = this.presentationsList[0].value;
      }
    },
  },

  mounted() {
    this.extractPresentationState(false);
    this.assignData();
    window.addEventListener('message', this.handlePresentationEvents);
  },

  beforeDestroy() {
    window.removeEventListener('message', this.handlePresentationEvents);
  },
};
</script>
